import React, { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  Forward,
  Handshake,
  Heart,
  MapPin,
  MessageCircle,
  MoreVertical,
  UserCheck,
  Users,
} from "lucide-react";

import API_BASE from "../../api";
import {
  isVideoPostMedia,
  resolvePostMediaSrc,
} from "../../utils/postMedia";
import { getShareCountForPost } from "../../utils/postShares";
import PostInfoActionIcon from "../post/PostInfoActionIcon";
import PaginationSentinel from "../common/PaginationSentinel";
import TranslatablePostCaption from "../post/TranslatablePostCaption";
import { isEditablePost } from "./edit-post/editPostPayloadBuilder";

const DEFAULT_AVATAR = "https://cdn-icons-png.flaticon.com/512/847/847969.png";
const BADGE_BASE_PATH = "/badges/";
const BADGE_EXTENSION = ".png";
const PROFILE_VIDEO_BACKDROP_CLOSE_GUARD_MS = 650;
const DEBUG_PROFILE_VIDEO = false;
const PROFILE_VIDEO_POSTER_CANDIDATES = [
  ["poster"],
  ["posterUrl"],
  ["poster_url"],
  ["thumbnail"],
  ["thumbnailUrl"],
  ["thumbnail_url"],
  ["previewUrl"],
  ["preview_url"],
  ["imageUrl"],
  ["image_url"],
  ["coverUrl"],
  ["cover_url"],
  ["media", "poster"],
  ["media", "posterUrl"],
  ["media", "poster_url"],
  ["media", "thumbnail"],
  ["media", "thumbnailUrl"],
  ["media", "thumbnail_url"],
  ["media", "previewUrl"],
  ["media", "preview_url"],
  ["media", "imageUrl"],
  ["media", "image_url"],
  ["media", "coverUrl"],
  ["media", "cover_url"],
  ["videoMetadata", "poster"],
  ["videoMetadata", "posterUrl"],
  ["videoMetadata", "poster_url"],
  ["videoMetadata", "thumbnail"],
  ["videoMetadata", "thumbnailUrl"],
  ["videoMetadata", "thumbnail_url"],
  ["videoMetadata", "previewUrl"],
  ["videoMetadata", "preview_url"],
  ["videoMetadata", "coverUrl"],
  ["videoMetadata", "cover_url"],
];

function shortenProfileVideoUrl(value) {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";

  try {
    const baseUrl =
      typeof window !== "undefined" ? window.location.origin : "http://localhost";
    const parsed = new URL(rawValue, baseUrl);
    const pathParts = parsed.pathname.split("/").filter(Boolean);
    const shortPath = pathParts.slice(-3).join("/");
    return shortPath || parsed.pathname || parsed.hostname;
  } catch {
    const withoutQuery = rawValue.split(/[?#]/)[0];
    return withoutQuery.length > 96
      ? `...${withoutQuery.slice(-96)}`
      : withoutQuery;
  }
}

function getProfileVideoDebugSnapshot(video) {
  if (!video) {
    return { hasVideo: false };
  }

  const error = video.error
    ? {
        code: video.error.code,
        message: video.error.message || "",
      }
    : null;

  return {
    hasVideo: true,
    src: shortenProfileVideoUrl(video.currentSrc || video.src),
    readyState: video.readyState,
    networkState: video.networkState,
    paused: video.paused,
    currentTime: Number.isFinite(video.currentTime)
      ? Number(video.currentTime.toFixed(3))
      : video.currentTime,
    error,
  };
}

function getProfileVideoEventPreviewVideo(event) {
  const currentTarget = event?.currentTarget;
  if (!currentTarget) return null;
  if (currentTarget.tagName === "VIDEO") return currentTarget;
  if (typeof currentTarget.querySelector === "function") {
    return currentTarget.querySelector(".rp-post__media--video");
  }
  return null;
}

function debugProfileVideo(message, data = {}) {
  if (!DEBUG_PROFILE_VIDEO || typeof console === "undefined") return;
  console.debug("[ProfileVideoDebug]", message, data);
}

function getNestedProfileVideoValue(source, path) {
  return path.reduce((current, key) => {
    if (!current || typeof current !== "object") return undefined;
    return current[key];
  }, source);
}

function resolveProfileVideoPosterUrl(value) {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";
  if (/^(https?:|data:|blob:)/i.test(rawValue)) return rawValue;
  return `${API_BASE}${rawValue}`;
}

function resolveProfileVideoPoster(post) {
  for (const path of PROFILE_VIDEO_POSTER_CANDIDATES) {
    const value = getNestedProfileVideoValue(post, path);
    const src = resolveProfileVideoPosterUrl(value);
    if (src) {
      return {
        src,
        field: path.join("."),
      };
    }
  }

  return {
    src: "",
    field: "",
  };
}

function normalizeTextValue(value) {
  return String(value || "").trim();
}

function hasMeaningfulValue(value) {
  const normalized = normalizeTextValue(value);
  return Boolean(normalized) && normalized !== "-";
}

function looksLikeCoordinateLabel(value) {
  return /^-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?$/.test(
    normalizeTextValue(value)
  );
}

function pickLocationLabel(...values) {
  for (const value of values) {
    const normalized = normalizeTextValue(value);
    if (
      normalized &&
      normalized !== "-" &&
      normalized.toLowerCase() !== "unknown" &&
      !looksLikeCoordinateLabel(normalized)
    ) {
      return normalized;
    }
  }

  return "";
}

function pickCountryFromLocationLabel(...values) {
  const label = pickLocationLabel(...values);
  if (!label) return "";

  const parts = label
    .split(",")
    .map(normalizeTextValue)
    .filter(Boolean);

  if (parts.length < 2) return "";

  return parts[parts.length - 1];
}

function resolveUserAvatarSrc(authorUser) {
  const avatar = normalizeTextValue(authorUser?.avatar);
  if (!avatar) return DEFAULT_AVATAR;
  return /^https?:\/\//i.test(avatar) ? avatar : `${API_BASE}${avatar}`;
}

function normalizeAvatarPosition(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 50;
  return Math.min(100, Math.max(0, parsed));
}

function resolveAvatarObjectPosition(authorUser) {
  const positionX = normalizeAvatarPosition(authorUser?.avatarPositionX);
  const positionY = normalizeAvatarPosition(authorUser?.avatarPositionY);
  return `${positionX}% ${positionY}%`;
}

function resolveAvatarScale(authorUser) {
  const parsed = Number(authorUser?.avatarScale);
  if (!Number.isFinite(parsed) || parsed <= 0) return 1;
  return parsed;
}

function buildBadgeUrl(badgeKey) {
  const normalizedKey = normalizeTextValue(badgeKey).toLowerCase();
  if (!normalizedKey) return "";
  const publicUrl = process.env.PUBLIC_URL || "";
  return `${publicUrl}${BADGE_BASE_PATH}${normalizedKey}${BADGE_EXTENSION}`;
}

function getPostLocationLabel(post) {
  const catchData = post?.catch || {};
  if (catchData.isLocationPublic === false) return "";

  const explicitCountry = pickLocationLabel(
    catchData.country,
    post?.country,
    catchData.historicalContext?.country,
    post?.historicalContext?.country
  );

  if (explicitCountry) return explicitCountry;

  return pickCountryFromLocationLabel(
    catchData.locationLabel,
    post?.locationLabel,
    catchData.historicalContext?.locationLabel,
    post?.historicalContext?.locationLabel,
    catchData.placeName,
    post?.placeName,
    catchData.location,
    post?.location
  );
}

function getPostDateLabel(post) {
  if (!post?.postDate) return "";

  const parsedDate = new Date(post.postDate);
  if (Number.isNaN(parsedDate.getTime())) return normalizeTextValue(post.postDate);

  return parsedDate.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function ProfileContentSections({ profile }) {
  const {
    t,
    user,
    isOwnProfile,
    posts,
    shareCountLookup,
    followersCount,
    followingCount,
    friendsCount,
    openComments,
    commentText,
    replyTo,
    replyText,
    usersMap,
    openPostMenuId,
    deletingPostId,
    isPostLikePending,
    highlightedCommentKey,
    postRefs,
    commentRefs,
    setCommentText,
    setReplyText,
    handleDeletePost,
    handleOpenEditPost,
    handleTogglePostMenu,
    handleLikePost,
    handleToggleComments,
    handleOpenShare,
    handleOpenInfo,
    handleLikeComment,
    handleToggleReply,
    handleLikeReply,
    handleSubmitReply,
    handleSubmitComment,
    profilePostsLoadingMore,
    profilePostsHasMore,
    loadMoreProfilePosts,
  } = profile;
  const [zoomVideo, setZoomVideo] = useState(null);
  const [zoomVideoPlayFailed, setZoomVideoPlayFailed] = useState(false);
  const [mobileProfileVideoViewport, setMobileProfileVideoViewport] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 768px)").matches
  );
  const [mobilePreviewMetadataVideoIds, setMobilePreviewMetadataVideoIds] =
    useState(() => new Set());
  const profileWallRef = useRef(null);
  const zoomVideoRef = useRef(null);
  const shouldAttemptZoomVideoPlayRef = useRef(false);
  const zoomVideoPlayAttemptInFlightRef = useRef(false);
  const zoomVideoOpenIdRef = useRef(0);
  const zoomVideoOpenedAtRef = useRef(0);
  const mobileVideoPreviewRefs = useRef({});
  const zoomVideoUrl = zoomVideo?.url || "";
  const zoomVideoInstanceId = zoomVideo?.instanceId || 0;

  const pauseVideoElement = useCallback((video, options = {}) => {
    if (!video) {
      return {
        before: getProfileVideoDebugSnapshot(video),
        after: getProfileVideoDebugSnapshot(video),
        error: null,
      };
    }

    const before = getProfileVideoDebugSnapshot(video);
    let errorDetails = null;
    try {
      video.pause();
      if (options.reset) {
        video.currentTime = 0;
      }
    } catch (error) {
      errorDetails = {
        name: error?.name || "",
        message: error?.message || "",
      };
    }

    return {
      before,
      after: getProfileVideoDebugSnapshot(video),
      error: errorDetails,
    };
  }, []);

  const hardStopVideoElement = useCallback((video, reason = "unspecified") => {
    const before = getProfileVideoDebugSnapshot(video);
    const beforeAttributeSrc = video?.getAttribute?.("src") || "";
    const beforeSources =
      video && typeof video.querySelectorAll === "function"
        ? Array.from(video.querySelectorAll("source"))
        : [];
    const result = {
      reason,
      before,
      beforeAttributeSrc: shortenProfileVideoUrl(beforeAttributeSrc),
      beforeSourceCount: beforeSources.length,
      pauseError: null,
      muteError: null,
      resetError: null,
      clearSourceError: null,
      loadCalled: false,
      loadError: null,
      afterAttributeSrc: "",
      after: null,
    };

    if (!video) return result;

    try {
      video.pause();
    } catch (error) {
      result.pauseError = {
        name: error?.name || "",
        message: error?.message || "",
      };
    }

    try {
      video.muted = true;
    } catch (error) {
      result.muteError = {
        name: error?.name || "",
        message: error?.message || "",
      };
    }

    try {
      video.currentTime = 0;
    } catch (error) {
      result.resetError = {
        name: error?.name || "",
        message: error?.message || "",
      };
    }

    try {
      video.removeAttribute("src");
      beforeSources.forEach((source) => {
        source.removeAttribute("src");
      });
    } catch (error) {
      result.clearSourceError = {
        name: error?.name || "",
        message: error?.message || "",
      };
    }

    result.afterAttributeSrc = video.getAttribute("src") || "";

    try {
      video.load();
      result.loadCalled = true;
    } catch (error) {
      result.loadError = {
        name: error?.name || "",
        message: error?.message || "",
      };
    }

    result.after = getProfileVideoDebugSnapshot(video);
    return result;
  }, []);

  const cleanupProfileVideoMediaSession = useCallback(
    (reason = "unspecified") => {
      if (typeof navigator === "undefined" || !("mediaSession" in navigator)) {
        return;
      }

      try {
        navigator.mediaSession.playbackState = "none";
      } catch (error) {
        debugProfileVideo("mediaSession playbackState cleanup failed", {
          reason,
          name: error?.name || "",
          message: error?.message || "",
        });
      }

      try {
        navigator.mediaSession.metadata = null;
      } catch (error) {
        debugProfileVideo("mediaSession metadata cleanup failed", {
          reason,
          name: error?.name || "",
          message: error?.message || "",
        });
      }
    },
    []
  );

  const pauseEmbeddedProfileVideos = useCallback(
    (options = {}) => {
      if (typeof document === "undefined") return;
      const allVideos = Array.from(
        document.querySelectorAll(
          ".profile-wall .rp-profile-post-card .rp-post__media--video"
        )
      );
      const viewerVideo = zoomVideoRef.current;
      allVideos
        .filter((video) => video !== viewerVideo)
        .forEach((video) => pauseVideoElement(video, options));
    },
    [pauseVideoElement]
  );

  const playZoomVideo = useCallback(
    (targetVideo) => {
      const video = targetVideo || zoomVideoRef.current;
      if (!video) return;

      try {
        const playInstanceId = zoomVideoInstanceId;
        const isCurrentPlayAttempt = () =>
          zoomVideoOpenIdRef.current === playInstanceId &&
          zoomVideoRef.current === video;
        const playPromise = video.play();

        if (playPromise && typeof playPromise.catch === "function") {
          playPromise
            .then(() => {
              if (!isCurrentPlayAttempt()) return;
              shouldAttemptZoomVideoPlayRef.current = false;
              zoomVideoPlayAttemptInFlightRef.current = false;
              setZoomVideoPlayFailed(false);
            })
            .catch((error) => {
              if (!isCurrentPlayAttempt()) {
                debugProfileVideo("video.play() rejected for stale viewer", {
                  instanceId: playInstanceId,
                  currentInstanceId: zoomVideoOpenIdRef.current,
                  name: error?.name || "",
                  message: error?.message || "",
                });
                return;
              }
              shouldAttemptZoomVideoPlayRef.current = false;
              zoomVideoPlayAttemptInFlightRef.current = false;
              setZoomVideoPlayFailed(true);
            });
        } else {
          shouldAttemptZoomVideoPlayRef.current = false;
          zoomVideoPlayAttemptInFlightRef.current = false;
          setZoomVideoPlayFailed(false);
        }
      } catch (error) {
        shouldAttemptZoomVideoPlayRef.current = false;
        zoomVideoPlayAttemptInFlightRef.current = false;
        setZoomVideoPlayFailed(true);
        debugProfileVideo("video.play() threw", {
          instanceId: zoomVideoInstanceId,
          name: error?.name || "",
          message: error?.message || "",
          video: getProfileVideoDebugSnapshot(video),
        });
      }
    },
    [zoomVideoInstanceId]
  );

  const attemptZoomVideoPlay = useCallback(
    (reason = "unspecified") => {
      if (!shouldAttemptZoomVideoPlayRef.current) return;
      if (zoomVideoPlayAttemptInFlightRef.current) {
        debugProfileVideo("play skipped: attempt in flight", { reason });
        return;
      }

      const video = zoomVideoRef.current;
      if (!video) return;

      zoomVideoPlayAttemptInFlightRef.current = true;
      playZoomVideo(video);
    },
    [playZoomVideo]
  );

  const openZoomVideo = useCallback(
    (videoSrc) => {
      const nextInstanceId = zoomVideoOpenIdRef.current + 1;
      zoomVideoOpenIdRef.current = nextInstanceId;
      zoomVideoOpenedAtRef.current =
        typeof performance !== "undefined" &&
        typeof performance.now === "function"
          ? performance.now()
          : Date.now();
      pauseEmbeddedProfileVideos({ reset: false, reason: "open_viewer" });
      shouldAttemptZoomVideoPlayRef.current = true;
      zoomVideoPlayAttemptInFlightRef.current = false;
      setZoomVideoPlayFailed(false);

      flushSync(() => {
        setZoomVideo({
          url: videoSrc,
          instanceId: nextInstanceId,
        });
      });
    },
    [pauseEmbeddedProfileVideos]
  );

  const closeZoomVideo = useCallback(
    (options = {}) => {
      const reason = options.reason || "unspecified";
      const now =
        typeof performance !== "undefined" &&
        typeof performance.now === "function"
          ? performance.now()
          : Date.now();
      const elapsedSinceOpen = now - zoomVideoOpenedAtRef.current;

      if (
        reason === "backdrop" &&
        elapsedSinceOpen >= 0 &&
        elapsedSinceOpen < PROFILE_VIDEO_BACKDROP_CLOSE_GUARD_MS
      ) {
        return;
      }

      shouldAttemptZoomVideoPlayRef.current = false;
      zoomVideoPlayAttemptInFlightRef.current = false;
      setZoomVideoPlayFailed(false);

      if (
        typeof document !== "undefined" &&
        document.fullscreenElement &&
        typeof document.exitFullscreen === "function"
      ) {
        try {
          const exitPromise = document.exitFullscreen();
          if (exitPromise && typeof exitPromise.catch === "function") {
            exitPromise.catch(() => {});
          }
        } catch {
          // Defensive only; the app viewer never requests native fullscreen.
        }
      }

      hardStopVideoElement(zoomVideoRef.current, "close_zoom_video");
      zoomVideoRef.current = null;
      cleanupProfileVideoMediaSession("close_zoom_video");
      setZoomVideo(null);
      zoomVideoOpenedAtRef.current = 0;
    },
    [cleanupProfileVideoMediaSession, hardStopVideoElement]
  );

  const handleProfileVideoTap = useCallback(
    (event, videoSrc) => {
      const previewVideo = getProfileVideoEventPreviewVideo(event);
      if (!mobileProfileVideoViewport) return;

      event.preventDefault();
      event.stopPropagation();
      pauseVideoElement(previewVideo, { reset: false });
      openZoomVideo(videoSrc);
    },
    [mobileProfileVideoViewport, openZoomVideo, pauseVideoElement]
  );

  const setZoomVideoNode = useCallback((node) => {
    zoomVideoRef.current = node;
  }, []);

  const handleZoomVideoReadyEvent = useCallback(
    () => {
      attemptZoomVideoPlay("viewer_ready");
    },
    [attemptZoomVideoPlay]
  );

  const handleZoomVideoPlayingEvent = useCallback(() => {
    shouldAttemptZoomVideoPlayRef.current = false;
    zoomVideoPlayAttemptInFlightRef.current = false;
    setZoomVideoPlayFailed(false);
  }, []);

  const handleZoomVideoFallbackPlay = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setZoomVideoPlayFailed(false);
      shouldAttemptZoomVideoPlayRef.current = true;
      zoomVideoPlayAttemptInFlightRef.current = false;
      attemptZoomVideoPlay("fallback_tap");
    },
    [attemptZoomVideoPlay]
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const updateMobileProfileVideoViewport = () => {
      setMobileProfileVideoViewport(mediaQuery.matches);
    };

    updateMobileProfileVideoViewport();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateMobileProfileVideoViewport);
      return () => {
        mediaQuery.removeEventListener(
          "change",
          updateMobileProfileVideoViewport
        );
      };
    }

    if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(updateMobileProfileVideoViewport);
      return () => {
        mediaQuery.removeListener(updateMobileProfileVideoViewport);
      };
    }

    return undefined;
  }, []);

  useEffect(() => {
    if (!mobileProfileVideoViewport) {
      setMobilePreviewMetadataVideoIds((current) =>
        current.size === 0 ? current : new Set()
      );
      return undefined;
    }

    const videoPreviewIds = new Set();
    posts.forEach((post) => {
        let enrichedPost = post;

        if (post.postType === "share" && post.sharedFrom) {
          const original = posts.find(
            (candidate) => candidate.id === post.sharedFrom.postId
          );
          if (original) {
            enrichedPost = {
              ...post,
              sharedPost: original,
            };
          }
        }

        const postId = String(post.id || "");
        if (isVideoPostMedia(enrichedPost)) {
          videoPreviewIds.add(`post:${postId}`);
        }
        if (isVideoPostMedia(enrichedPost.sharedPost)) {
          const sharedPostId = String(
            enrichedPost.sharedPost?.id || `${postId || "post"}-shared`
          );
          videoPreviewIds.add(`shared:${postId}:${sharedPostId}`);
        }
      });

    setMobilePreviewMetadataVideoIds((current) => {
      const next = new Set(
        Array.from(current).filter((videoId) => videoPreviewIds.has(videoId))
      );
      return next.size === current.size ? current : next;
    });

    const previewEntries = Object.entries(mobileVideoPreviewRefs.current).filter(
      ([videoId, element]) => videoPreviewIds.has(videoId) && element
    );

    if (previewEntries.length === 0) return undefined;

    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      setMobilePreviewMetadataVideoIds((current) => {
        const next = new Set(current);
        previewEntries.forEach(([videoId]) => next.add(videoId));
        return next;
      });
      return undefined;
    }

    const observer = new window.IntersectionObserver(
      (entries) => {
        const visibleVideoIds = entries
          .filter((entry) => entry.isIntersecting || entry.intersectionRatio > 0)
          .map((entry) =>
            String(
              entry.target.getAttribute("data-profile-video-preview-id") || ""
            )
          )
          .filter(Boolean);

        if (visibleVideoIds.length === 0) return;

        setMobilePreviewMetadataVideoIds((current) => {
          let changed = false;
          const next = new Set(current);
          visibleVideoIds.forEach((videoId) => {
            if (!next.has(videoId)) {
              next.add(videoId);
              changed = true;
            }
          });

          return changed ? next : current;
        });
      },
      {
        root: null,
        rootMargin: "600px 0px",
        threshold: 0.01,
      }
    );

    previewEntries.forEach(([, element]) => observer.observe(element));

    return () => observer.disconnect();
  }, [mobileProfileVideoViewport, posts, user?.username]);

  useEffect(() => {
    if (!zoomVideo) return undefined;
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }

    const body = document.body;
    const documentElement = document.documentElement;
    const scrollContainer = profileWallRef.current?.closest(".profile-overlay");
    if (!body || !documentElement) return undefined;

    const scrollY =
      window.scrollY ||
      window.pageYOffset ||
      documentElement.scrollTop ||
      body.scrollTop ||
      0;
    const scrollContainerTop = scrollContainer?.scrollTop || 0;
    const previousBodyStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
      overscrollBehavior: body.style.overscrollBehavior,
    };
    const previousDocumentStyles = {
      overflow: documentElement.style.overflow,
      overscrollBehavior: documentElement.style.overscrollBehavior,
    };
    const previousScrollContainerStyles = scrollContainer
      ? {
          overflow: scrollContainer.style.overflow,
          overscrollBehavior: scrollContainer.style.overscrollBehavior,
        }
      : null;

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    documentElement.style.overflow = "hidden";
    documentElement.style.overscrollBehavior = "none";
    if (scrollContainer) {
      scrollContainer.style.overflow = "hidden";
      scrollContainer.style.overscrollBehavior = "none";
    }

    return () => {
      body.style.position = previousBodyStyles.position;
      body.style.top = previousBodyStyles.top;
      body.style.left = previousBodyStyles.left;
      body.style.right = previousBodyStyles.right;
      body.style.width = previousBodyStyles.width;
      body.style.overflow = previousBodyStyles.overflow;
      body.style.overscrollBehavior = previousBodyStyles.overscrollBehavior;
      documentElement.style.overflow = previousDocumentStyles.overflow;
      documentElement.style.overscrollBehavior =
        previousDocumentStyles.overscrollBehavior;
      if (scrollContainer && previousScrollContainerStyles) {
        scrollContainer.style.overflow = previousScrollContainerStyles.overflow;
        scrollContainer.style.overscrollBehavior =
          previousScrollContainerStyles.overscrollBehavior;
        scrollContainer.scrollTop = scrollContainerTop;
      }

      try {
        window.scrollTo(0, scrollY);
      } catch {
        // Style restoration above still keeps the page usable.
      }
    };
  }, [zoomVideo]);

  return (
    <div className="profile-wall" ref={profileWallRef}>
      <div className="profile-wall__intro">
        <div className="profile-stats-row" aria-label={t("profile_social_stats", { defaultValue: "Profile social stats" })}>
          <div className="profile-stat-card">
            <span className="profile-stat-card__icon" aria-hidden="true">
              <Users size={20} strokeWidth={2.2} />
            </span>
            <strong>{followersCount}</strong>
            <span>{t("followers")}</span>
          </div>
          <div className="profile-stat-card">
            <span className="profile-stat-card__icon" aria-hidden="true">
              <UserCheck size={20} strokeWidth={2.2} />
            </span>
            <strong>{followingCount}</strong>
            <span>{t("following")}</span>
          </div>
          <div className="profile-stat-card">
            <span className="profile-stat-card__icon" aria-hidden="true">
              <Handshake size={20} strokeWidth={2.2} />
            </span>
            <strong>{friendsCount}</strong>
            <span>{t("friends")}</span>
          </div>
        </div>

        <div className="profile-wall-label">
          <span className="profile-wall-label__text">
            {t("profile_fishing_activity")}
          </span>
          <img
            className="profile-wall-label__banner"
            src="/banner/myfishingactivity.png"
            alt={t("profile_fishing_activity", {
              defaultValue: "My Fishing Activity",
            })}
            onError={(event) => {
              event.currentTarget.style.display = "none";
              event.currentTarget.previousElementSibling?.classList.add(
                "profile-wall-label__text--fallback"
              );
            }}
          />
        </div>
      </div>

      <div className="profile-activity-list">
        {posts.map((post) => {
          let enrichedPost = post;

          if (post.postType === "share" && post.sharedFrom) {
            const original = posts.find((candidate) => candidate.id === post.sharedFrom.postId);
            if (original) {
              enrichedPost = {
                ...post,
                sharedPost: original,
              };
            }
          }
          const sharedPostMediaSrc = resolvePostMediaSrc(enrichedPost.sharedPost);
          const postMediaSrc = resolvePostMediaSrc(enrichedPost);
          const postHasVideoMedia = isVideoPostMedia(enrichedPost);
          const sharedPostHasVideoMedia = isVideoPostMedia(enrichedPost.sharedPost);
          const postId = String(post.id || "");
          const sharedPostId = String(
            enrichedPost.sharedPost?.id || `${postId || "post"}-shared`
          );
          const postVideoPreviewId = `post:${postId}`;
          const sharedPostVideoPreviewId = `shared:${postId}:${sharedPostId}`;
          const postVideoPoster = postHasVideoMedia
            ? resolveProfileVideoPoster(enrichedPost)
            : { src: "", field: "" };
          const sharedPostVideoPoster = sharedPostHasVideoMedia
            ? resolveProfileVideoPoster(enrichedPost.sharedPost)
            : { src: "", field: "" };
          const postVideoPreviewPreload = mobileProfileVideoViewport
            ? postVideoPoster.src
              ? "none"
              : mobilePreviewMetadataVideoIds.has(postVideoPreviewId)
              ? "metadata"
              : "none"
            : "metadata";
          const sharedPostVideoPreviewPreload = mobileProfileVideoViewport
            ? sharedPostVideoPoster.src
              ? "none"
              : mobilePreviewMetadataVideoIds.has(sharedPostVideoPreviewId)
              ? "metadata"
              : "none"
            : "metadata";
          const authorProgression = user?.progression || enrichedPost?.progression || {};
          const authorRankName = normalizeTextValue(
            authorProgression.currentRankName || authorProgression.rankName
          );
          const authorBadgeUrl = buildBadgeUrl(
            authorProgression.currentBadgeKey || authorProgression.badgeKey
          );
          const authorAvatarStyle = {
            objectPosition: resolveAvatarObjectPosition(user),
            transform: `scale(${resolveAvatarScale(user)})`,
          };
          const dateLabel = getPostDateLabel(enrichedPost);
          const locationLabel = getPostLocationLabel(enrichedPost);
          const likeCount = post.likes?.length || 0;
          const commentCount = post.comments?.length || 0;
          const shareCount = getShareCountForPost(post, shareCountLookup);
          const currentUserHasLikedPost = Boolean(
            user?.id &&
              Array.isArray(post.likes) &&
              post.likes.some((likeUserId) => String(likeUserId) === String(user.id))
          );
          const postLikePending = isPostLikePending(post.id);
          const hasPostText = Boolean(
            enrichedPost.sharedPost || hasMeaningfulValue(enrichedPost.caption)
          );
          const canEditPost = isEditablePost(post);
          const postContentClassName = [
            "rp-post__content",
            postMediaSrc ? "rp-post__content--with-media" : "",
            postMediaSrc && !hasPostText ? "rp-post__content--media-only" : "",
          ]
            .filter(Boolean)
            .join(" ");
          const postActionsClassName = [
            "post-actions",
            "rp-post__actions",
            enrichedPost.postType !== "opinion"
              ? "rp-post__actions--has-info"
              : "rp-post__actions--no-info",
          ].join(" ");

            return (
              <div
                key={post.id}
                className="post rp-post profile-post rp-profile-post-card"
                ref={(node) => {
                  if (node) {
                    postRefs.current[post.id] = node;
                  } else {
                    delete postRefs.current[post.id];
                  }
                }}
                tabIndex={-1}
              >
              <div className="profile-post-header rp-post__header">
                <button
                  type="button"
                  className="rp-post__avatar-button"
                  disabled
                  aria-label={t("profile_picture")}
                >
                  <img
                    src={resolveUserAvatarSrc(user)}
                    alt={t("profile_picture")}
                    className="rp-post__avatar"
                    style={authorAvatarStyle}
                  />
                </button>

                <div className="profile-post-header-main rp-post__meta">
                  <div className="rp-post__name-row">
                    <h3 className="profile-post-username rp-post__name">
                      {enrichedPost.user}
                    </h3>
                    {authorBadgeUrl ? (
                      <img
                        src={authorBadgeUrl}
                        alt={
                          authorRankName
                            ? t("profile_rank_badge_alt", {
                                defaultValue: "{{rankName}} badge",
                                rankName: authorRankName,
                              })
                            : ""
                        }
                        className="rp-post__rank-badge"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    ) : null}
                  </div>

                  {authorRankName ? (
                    <span className="rp-post__rank-text">{authorRankName}</span>
                  ) : null}
                </div>

                <div className="rp-post__header-trail">
                  {dateLabel ? (
                    <span className="profile-post-date rp-post__time-row">
                      <span className="rp-post__time">{dateLabel}</span>
                    </span>
                  ) : null}
                  {locationLabel ? (
                    <span className="rp-post__location">
                      <MapPin size={11} strokeWidth={2.1} aria-hidden="true" />
                      <span>{locationLabel}</span>
                    </span>
                  ) : null}
                </div>

                {isOwnProfile && (
                  <div className="profile-post-owner-actions rp-post__owner-actions">
                    <button
                      type="button"
                      className="profile-post-menu-btn rp-post__menu-btn"
                      aria-label={t("profile_post_actions")}
                      aria-haspopup="menu"
                      aria-expanded={openPostMenuId === post.id}
                      onClick={() => handleTogglePostMenu(post.id)}
                    >
                      <MoreVertical size={22} strokeWidth={2.4} aria-hidden="true" />
                    </button>

                    {openPostMenuId === post.id && (
                      <div className="profile-post-menu rp-post__menu" role="menu">
                        {canEditPost && (
                          <button
                            type="button"
                            role="menuitem"
                            className="profile-post-menu-item rp-post__menu-item"
                            disabled={deletingPostId === post.id}
                            onClick={() => handleOpenEditPost(post)}
                          >
                            {t("edit")}
                          </button>
                        )}
                        <button
                          type="button"
                          role="menuitem"
                          className="profile-post-menu-item profile-post-menu-item-danger rp-post__menu-item rp-post__menu-item--danger"
                          disabled={deletingPostId === post.id}
                          onClick={() => handleDeletePost(post.id)}
                        >
                          {deletingPostId === post.id
                            ? `${t("delete")}...`
                            : t("delete")}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className={postContentClassName}>
                {postMediaSrc ? (
                  <div className="rp-post__image">
                    {postHasVideoMedia ? (
                      <div
                        className="rp-post__video-shell profile-post-video-shell"
                        ref={(element) => {
                          if (!postVideoPreviewId) return;
                          if (element) {
                            mobileVideoPreviewRefs.current[postVideoPreviewId] =
                              element;
                          } else {
                            delete mobileVideoPreviewRefs.current[
                              postVideoPreviewId
                            ];
                          }
                        }}
                        role={mobileProfileVideoViewport ? "button" : undefined}
                        tabIndex={mobileProfileVideoViewport ? 0 : undefined}
                        aria-label={
                          mobileProfileVideoViewport
                            ? t("open_video")
                            : undefined
                        }
                        onPointerUpCapture={(event) =>
                          handleProfileVideoTap(event, postMediaSrc)
                        }
                        onKeyDown={(event) => {
                          if (!mobileProfileVideoViewport) return;
                          if (event.key !== "Enter" && event.key !== " ") return;
                          handleProfileVideoTap(event, postMediaSrc);
                        }}
                        data-profile-video-preview-id={postVideoPreviewId}
                      >
                        <video
                          src={postMediaSrc}
                          poster={postVideoPoster.src || undefined}
                          controls={!mobileProfileVideoViewport}
                          controlsList="nodownload noplaybackrate"
                          disablePictureInPicture
                          playsInline
                          preload={postVideoPreviewPreload}
                          className="profile-media rp-post__media rp-post__media--video profile-post-video-preview"
                          data-profile-video-post-id={postVideoPreviewId}
                          onPlay={(event) => {
                            if (!mobileProfileVideoViewport) return;
                            pauseVideoElement(event.currentTarget, {
                              reset: false,
                            });
                          }}
                        />
                        {mobileProfileVideoViewport ? (
                          <span
                            className="profile-post-video-play"
                            aria-hidden="true"
                          ></span>
                        ) : null}
                      </div>
                    ) : (
                      <img
                        src={postMediaSrc}
                        alt={t("post_media")}
                        className="profile-media post-image rp-post__media rp-post__media--image"
                      />
                    )}
                  </div>
                ) : null}

                {hasPostText ? (
                  <div className="rp-post__text-panel">
                    {enrichedPost.postType === "share" && enrichedPost.sharedPost ? (
                      <div className="shared-post-wrapper rp-post__shared">
                        <p className="shared-by rp-post__shared-by">
                          {'\u{1F501}'} <strong>{enrichedPost.user}</strong> {t("shared")}
                        </p>

                        {enrichedPost.caption ? (
                          <p className="shared-text rp-post__shared-text">
                            {enrichedPost.caption}
                          </p>
                        ) : null}

                        <div className="shared-original rp-post__shared-original">
                          <div className="shared-original-header">
                            <strong>{enrichedPost.sharedPost.user}</strong>

                            {enrichedPost.sharedPost.postDate ? (
                              <span className="shared-original-date rp-post__shared-time">
                                {new Date(enrichedPost.sharedPost.postDate).toLocaleString(
                                  [],
                                  {
                                    dateStyle: "medium",
                                    timeStyle: "short",
                                  }
                                )}
                              </span>
                            ) : null}
                          </div>

                          <p>{enrichedPost.sharedPost.caption}</p>

                          {sharedPostMediaSrc &&
                            (sharedPostHasVideoMedia ? (
                              <div
                                className="rp-post__video-shell profile-post-video-shell profile-post-video-shell--shared"
                                ref={(element) => {
                                  if (!sharedPostVideoPreviewId) return;
                                  if (element) {
                                    mobileVideoPreviewRefs.current[
                                      sharedPostVideoPreviewId
                                    ] = element;
                                  } else {
                                    delete mobileVideoPreviewRefs.current[
                                      sharedPostVideoPreviewId
                                    ];
                                  }
                                }}
                                role={
                                  mobileProfileVideoViewport
                                    ? "button"
                                    : undefined
                                }
                                tabIndex={
                                  mobileProfileVideoViewport ? 0 : undefined
                                }
                                aria-label={
                                  mobileProfileVideoViewport
                                    ? t("open_video")
                                    : undefined
                                }
                                onPointerUpCapture={(event) =>
                                  handleProfileVideoTap(
                                    event,
                                    sharedPostMediaSrc
                                  )
                                }
                                onKeyDown={(event) => {
                                  if (!mobileProfileVideoViewport) return;
                                  if (
                                    event.key !== "Enter" &&
                                    event.key !== " "
                                  ) {
                                    return;
                                  }
                                  handleProfileVideoTap(
                                    event,
                                    sharedPostMediaSrc
                                  );
                                }}
                                data-profile-video-preview-id={
                                  sharedPostVideoPreviewId
                                }
                              >
                                <video
                                  src={sharedPostMediaSrc}
                                  poster={sharedPostVideoPoster.src || undefined}
                                  controls={!mobileProfileVideoViewport}
                                  controlsList="nodownload noplaybackrate"
                                  disablePictureInPicture
                                  playsInline
                                  preload={sharedPostVideoPreviewPreload}
                                  className="profile-media profile-media-spaced rp-post__media rp-post__media--video profile-post-video-preview"
                                  data-profile-video-post-id={
                                    sharedPostVideoPreviewId
                                  }
                                  onPlay={(event) => {
                                    if (!mobileProfileVideoViewport) return;
                                    pauseVideoElement(event.currentTarget, {
                                      reset: false,
                                    });
                                  }}
                                />
                                {mobileProfileVideoViewport ? (
                                  <span
                                    className="profile-post-video-play"
                                    aria-hidden="true"
                                  ></span>
                                ) : null}
                              </div>
                            ) : (
                              <img
                                src={sharedPostMediaSrc}
                                alt={t("post_media")}
                                className="profile-media profile-media-spaced post-image rp-post__media rp-post__media--image"
                              />
                            ))}
                        </div>
                      </div>
                    ) : (
                      <TranslatablePostCaption
                        postId={enrichedPost.id}
                        postType={enrichedPost.postType}
                        text={enrichedPost.caption}
                      />
                    )}
                  </div>
                ) : null}
              </div>

              {false && post.catch && (
                <div className="catch-info">
                  <div>
                    <strong>{post.catch.fish}</strong>
                  </div>
                  {post.catch.size && <div>{'\u{1F4CF}'} {post.catch.size}</div>}
                  {post.catch.weight && <div>{'\u2696'}{'\uFE0F'} {post.catch.weight}</div>}
                </div>
              )}

              <div className={postActionsClassName}>
                <button
                  type="button"
                  className={[
                    "rp-post__action-btn",
                    currentUserHasLikedPost ? "rp-post__action-btn--liked" : "",
                    postLikePending ? "rp-post__action-btn--pending" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  title={t("like")}
                  aria-label={t("like")}
                  onClick={() => handleLikePost(post)}
                  disabled={postLikePending}
                  aria-busy={postLikePending}
                >
                  <span className="rp-post__action-icon" aria-hidden="true">
                    <Heart
                      size={24}
                      strokeWidth={2.2}
                      fill={currentUserHasLikedPost ? "currentColor" : "none"}
                    />
                  </span>
                  <span className="rp-post__action-copy rp-post__action-copy--desktop">
                    {t("like")} ({likeCount})
                  </span>
                  <span className="rp-post__action-count" aria-hidden="true">
                    {likeCount}
                  </span>
                </button>

                <button
                  type="button"
                  className="rp-post__action-btn"
                  title={t("comment")}
                  aria-label={t("comment")}
                  onClick={() => handleToggleComments(post.id)}
                >
                  <span className="rp-post__action-icon" aria-hidden="true">
                    <MessageCircle size={24} strokeWidth={2.2} />
                  </span>
                  <span className="rp-post__action-copy rp-post__action-copy--desktop">
                    {t("comment")} ({commentCount})
                  </span>
                  <span className="rp-post__action-count" aria-hidden="true">
                    {commentCount}
                  </span>
                </button>

                <button
                  type="button"
                  className="rp-post__action-btn rp-post__action-btn--share"
                  title={t("share")}
                  aria-label={t("share")}
                  onClick={() => handleOpenShare(post)}
                >
                  <span className="rp-post__action-icon" aria-hidden="true">
                    <Forward size={24} strokeWidth={2.25} />
                  </span>
                  <span className="rp-post__action-copy rp-post__action-copy--desktop">
                    {t("share")} ({shareCount})
                  </span>
                </button>

                {enrichedPost.postType !== "opinion" && (
                  <button
                    type="button"
                    className="rp-post__action-btn rp-post__action-btn--info"
                    title={t("info")}
                    aria-label={t("info")}
                    onClick={() => handleOpenInfo(post)}
                  >
                    <PostInfoActionIcon />
                    <span className="rp-post__action-copy rp-post__action-copy--desktop">
                      {t("info")}
                    </span>
                  </button>
                )}
              </div>
              {openComments === post.id && (
                <div className="post-comments rp-post__comments">
                  {(post.comments || []).map((comment) => {
                    const commentUser = usersMap[comment.userId];
                    const commentKey = `${post.id}:${comment.id}`;

                    return (
                      <div
                        key={comment.id}
                        className={`post-comment${
                          highlightedCommentKey === commentKey
                            ? " comment-focus-target"
                            : ""
                        }`}
                        ref={(node) => {
                          if (node) {
                            commentRefs.current[commentKey] = node;
                          } else {
                            delete commentRefs.current[commentKey];
                          }
                        }}
                        tabIndex={-1}
                      >
                        <div className="post-comment-header">
                          <img
                            src={
                              commentUser?.avatar
                                ? `${API_BASE}${commentUser.avatar}`
                                : "https://cdn-icons-png.flaticon.com/512/847/847969.png"
                            }
                            alt={t("profile_picture")}
                            className="post-comment-avatar"
                          />
                          <strong>{comment.username}</strong>
                        </div>

                        <div className="post-comment-text">{comment.text}</div>

                        <div className="post-comment-actions">
                          <button
                            className="comment-like-btn"
                            onClick={() => handleLikeComment(post.id, comment.id)}
                          >
                            {'\u{1F44D}'} {t("like")} ({comment.likes?.length || 0})
                          </button>

                          <button
                            className="comment-reply-btn"
                            onClick={() => handleToggleReply(comment.id)}
                          >
                            {'\u{1F4AC}'} {t("reply")}
                          </button>
                        </div>

                        {(comment.replies || []).map((reply) => (
                          <div key={reply.id} className="post-reply">
                            <div className="post-reply-text">
                              <strong>{reply.username}</strong> {reply.text}
                            </div>
                            <button
                              className="reply-like-btn"
                              onClick={() =>
                                handleLikeReply(post.id, comment.id, reply.id)
                              }
                            >
                              {'\u{1F44D}'} {t("like")} ({reply.likes?.length || 0})
                            </button>
                          </div>
                        ))}

                        {replyTo === comment.id && (
                          <div className="post-reply-form">
                            <input
                              type="text"
                              value={replyText}
                              placeholder={t("write_reply")}
                              onChange={(event) => setReplyText(event.target.value)}
                            />
                            <button
                              onClick={() =>
                                handleSubmitReply(post.id, comment.id)
                              }
                            >
                              {t("send")}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  <div className="post-comment-form">
                    <input
                      type="text"
                      value={commentText}
                      placeholder={t("write_comment")}
                      onChange={(event) => setCommentText(event.target.value)}
                    />
                    <button onClick={() => handleSubmitComment(post.id)}>
                      {t("send")}
                    </button>
                  </div>
                </div>
              )}
              </div>
            );
          })}
      </div>
      <PaginationSentinel
        enabled={Boolean(user?.id) && profilePostsHasMore}
        loading={profilePostsLoadingMore}
        onLoadMore={loadMoreProfilePosts}
        loadingLabel={t("loading", { defaultValue: "Loading…" })}
        testId="own-profile-pagination-sentinel"
      />
      {zoomVideo ? (
        <div
          className="profile-post-video-viewer"
          onClick={(event) => {
            if (event.target !== event.currentTarget) return;
            closeZoomVideo({ reason: "backdrop" });
          }}
        >
          <button
            type="button"
            className="profile-post-video-viewer__close"
            aria-label={t("close")}
            onClick={(event) => {
              event.stopPropagation();
              closeZoomVideo({ reason: "close_button" });
            }}
          >
            {"\u2715"}
          </button>

          <video
            key={`${zoomVideoInstanceId}:${zoomVideoUrl}`}
            ref={setZoomVideoNode}
            src={zoomVideoUrl}
            controls
            controlsList="nodownload noplaybackrate"
            disablePictureInPicture
            playsInline
            autoPlay
            preload="auto"
            className="profile-post-video-viewer__video"
            onLoadedMetadata={handleZoomVideoReadyEvent}
            onCanPlay={handleZoomVideoReadyEvent}
            onPlaying={handleZoomVideoPlayingEvent}
            onClick={(event) => event.stopPropagation()}
          />
          {zoomVideoPlayFailed ? (
            <button
              type="button"
              className="profile-post-video-viewer__fallback"
              onClick={handleZoomVideoFallbackPlay}
            >
              {t("tap_to_play")}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
